import React, { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import styled from 'styled-components';

import { useEnableSchema } from '@rover/components/src/microdata';
import dataLayer from '@rover/globals/dataLayer';
import { Flex, Text } from '@rover/kibble/core';
import Avatar from '@rover/kibble/patterns/Avatar';

import HighlightedReviewText from './HighlightedReviewText';
import { SEARCH_RESULT_CARD_Z_INDEX } from './SearchResultCard.constants';

const EXPANDED_LINE_CLAMP = 6;
const DEFAULT_LINE_CLAMP = 2;

export type Props = {
  reviewText?: string | null;
  /** Passages of `reviewText` that matched the searcher's pets, emphasized in full. Plain text, not markup. */
  reviewHighlights?: string[] | null;
  description?: string | null;
  reviewPetPhotoUrl?: string | null;
  expandedLineClampLevel?: number;
  defaultLineClampLevel?: number;
};

// eslint-disable-next-line rover/prefer-kibble-components
const MoreButton = styled.button`
  position: absolute;
  cursor: pointer;
  background: none;
  border: 0 none;
  padding: 12px 0;
  margin: 0;
  right: 0;
  bottom: -12px;
  z-index: ${SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE.toString()};
`;
// eslint-disable-next-line rover/prefer-kibble-components
const MoreButtonText = styled.span`
  display: inline;
  padding-left: 9ch;
  text-align: right;
  background: linear-gradient(to right, rgba(18, 18, 18, 0), rgba(255, 255, 255, 1) 9ch);
  color: ${({ theme }) => theme.colors.blue['600']};
  &:hover {
    color: ${({ theme }) => theme.colors.blue['700']};
  }
`;

// eslint-disable-next-line rover/prefer-kibble-components
const ExpandableReview = styled.p<{
  isExpanded: boolean;
  expandedLineClampLevel: number;
  defaultLineClampLevel: number;
}>`
  width: 100%;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: ${(props) =>
    props.isExpanded ? props.expandedLineClampLevel : props.defaultLineClampLevel};
  -webkit-box-orient: vertical;
  margin-bottom: 0px;
  margin-top: 0px;
`;

const FeaturedReview = ({
  description,
  reviewText,
  reviewHighlights,
  reviewPetPhotoUrl,
  expandedLineClampLevel = EXPANDED_LINE_CLAMP,
  defaultLineClampLevel = DEFAULT_LINE_CLAMP,
}: Props): React.ReactElement => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  // Whether the review is longer than the collapsed clamp shows, which is the only
  // thing measurement decides. Expanding is the reader's, so nothing here can undo it.
  // It starts true because the server cannot measure: the first measurement on the
  // client is what hides the button on a review that already fits.
  const [isOverflowing, setIsOverflowing] = useState<boolean>(true);
  const expandableReviewText = useRef<HTMLParagraphElement>(null);
  const enableSchema = useEnableSchema();

  const measureOverflow = (): void => {
    if (!expandableReviewText.current) return;
    const lineHeight = parseFloat(getComputedStyle(expandableReviewText.current).lineHeight);
    // scrollHeight covers the text the line clamp hides, so this is the full line count
    const actualLineCount = Math.round(expandableReviewText.current.scrollHeight / lineHeight);
    setIsOverflowing(actualLineCount > defaultLineClampLevel);
  };

  // The observers below are built once, so they reach the current measurement through
  // a ref rather than the one captured on mount.
  const measureOverflowRef = useRef(measureOverflow);
  measureOverflowRef.current = measureOverflow;

  useEffect(() => {
    // If the observer is undefined, exit early and don't init them
    if (typeof ResizeObserver === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return () => {};
    }

    const reviewNode = expandableReviewText.current;
    if (!process.env.JS_ENV_CLIENT || !reviewNode) {
      return () => {};
    }

    const resizeObserver = new ResizeObserver(() => {
      measureOverflowRef.current();
    });
    // This observer ensures only the reviews that are in view do resize checks
    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          resizeObserver.observe(reviewNode);
        } else {
          resizeObserver.disconnect();
        }
      });
    });

    intersectionObserver.observe(reviewNode);

    return () => {
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  const hasReviewText = !!reviewText;

  const shouldShowReviewerPetPhoto = hasReviewText && reviewPetPhotoUrl;

  return (
    <Flex mt="0x" marginBottom="3x" position="relative">
      {shouldShowReviewerPetPhoto && (
        <Avatar
          imageUrl={reviewPetPhotoUrl}
          type="person"
          size="small"
          marginRight="2x"
          sx={{ flexShrink: 0 }}
        />
      )}
      <ExpandableReview
        ref={expandableReviewText}
        isExpanded={isExpanded}
        expandedLineClampLevel={expandedLineClampLevel}
        defaultLineClampLevel={defaultLineClampLevel}
      >
        {hasReviewText ? (
          <Text size="100" data-testid="reviewText">
            &ldquo;
            <HighlightedReviewText text={reviewText} highlights={reviewHighlights} size="100" />
            &rdquo;
          </Text>
        ) : (
          <>
            <Text size="100">
              <Trans>About:</Trans>{' '}
            </Text>
            <span {...(enableSchema ? { itemProp: 'description' } : {})}>{description}</span>
          </>
        )}
      </ExpandableReview>
      {isOverflowing && !isExpanded && (
        <MoreButton
          data-testid="read-more-button"
          onClick={() => {
            setIsExpanded(true);
            dataLayer?.push({
              event: 'sitter-card-review-read-more-button-click',
            });
          }}
        >
          <MoreButtonText>
            <Trans>Read more</Trans>
          </MoreButtonText>
        </MoreButton>
      )}
    </Flex>
  );
};

export default FeaturedReview;
